"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Sparkles, Copy, Share2, Clock, ChevronDown } from "lucide-react"
import React from "react"
import { useLanguage } from "@/contexts/language-context"

const SectionWrapper = ({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) => (
  <Card className="glass dark:glass-dark shadow-sm border border-slate-200/60 dark:border-zinc-700/60 overflow-hidden">
    <CardHeader className="pb-4">
      <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-indigo-900/40 text-blue-600 dark:text-indigo-300 text-xs font-medium">UI</span>
        {title}
      </CardTitle>
      <p className="text-xs text-muted-foreground leading-relaxed max-w-prose">{subtitle}</p>
    </CardHeader>
    <CardContent className="pt-0">{children}</CardContent>
  </Card>
)

export const ScenarioTemplatesPreview = () => {
  const { t } = useLanguage()
  const scenarios = ["Email", "Message", "Workplace", "School", "Business", "Interview", "Presentation"]
  const templates = [
    { name: "Follow‑up Email", scenario: "Email" },
    { name: "Interview Thank‑You", scenario: "Interview" },
    { name: "Project Update", scenario: "Workplace" },
    { name: "Sales Outreach", scenario: "Business" }
  ]
  const SkeletonTag = () => (
    <div className="h-2.5 w-14 rounded bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 dark:from-indigo-800 dark:via-indigo-700 dark:to-indigo-800 animate-pulse" />
  )
  return (
    <SectionWrapper
      title={t('preview.section.scenarioTemplates.title') || 'Scenario & Templates'}
      subtitle={t('preview.section.scenarioTemplates.subtitle') || 'Quickly pick a scenario or apply a saved template. Templates prefill description / target / tone so you enter high-quality generation instantly.'}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {scenarios.map(s => (
            <button key={s} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white dark:bg-zinc-800/60 border border-slate-200 dark:border-zinc-700 hover:border-blue-400 hover:text-blue-600 dark:hover:border-indigo-400 transition">
              {s}
            </button>
          ))}
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-white dark:from-indigo-950/40 dark:to-zinc-900/40 border border-blue-100/70 dark:border-indigo-800/40 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold tracking-tight">{t('preview.templates.my') || 'My Templates'}</h4>
            <Button size="sm" className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700" disabled>{t('preview.templates.new') || 'New'}</Button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {templates.map(t => (
              <div key={t.name} className="group p-3 rounded-lg bg-white/70 dark:bg-zinc-800/60 backdrop-blur border border-slate-200 dark:border-zinc-700 hover:shadow-sm hover:border-blue-300 dark:hover:border-indigo-400 transition cursor-pointer">
                <p className="text-xs font-medium text-gray-800 dark:text-gray-200 mb-2 truncate" aria-label={t.name}>{t.name}</p>
                <SkeletonTag />
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionWrapper>
  )
}

export const CustomInputsPreview = () => {
  const { t } = useLanguage()
  const Line = ({ w }: { w: string }) => (
    <div className="h-3 rounded bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 dark:from-indigo-800 dark:via-indigo-700 dark:to-indigo-800 animate-pulse" style={{ width: w }} />
  )
  return (
    <SectionWrapper
      title={t('preview.section.customInputs.title') || 'Custom Inputs'}
      subtitle={t('preview.section.customInputs.subtitle') || 'Core input + optional description + advanced Scenario / Target / Tone selectors. Combine freely for context-aware results.'}
    >
      <div className="space-y-4">
        <div className="relative">
          <div className="rounded-xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/60 p-3 min-h-[110px] space-y-2">
            <Line w="92%" />
            <Line w="80%" />
            <Line w="65%" />
          </div>
          <div className="absolute -top-2 -left-2 px-2 py-0.5 rounded-md bg-blue-600 text-white text-[10px] font-medium shadow">INPUT</div>
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          {['preview.field.scenario','preview.field.target','preview.field.tone'].map(key => (
            <button key={key} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-white dark:bg-zinc-800/60 border border-slate-200 dark:border-zinc-700 hover:border-blue-400 dark:hover:border-indigo-400 transition">
              <span>{t(key) || key.split('.').pop()}: {t('preview.auto') || 'Auto'}</span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            </button>
          ))}
          <button className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-white dark:bg-zinc-800/60 border border-slate-200 dark:border-zinc-700 hover:border-blue-400 dark:hover:border-indigo-400 transition">
            <span>{t('dropdown.useTemplates') || 'Template'}</span>
            <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-zinc-600 bg-gray-50/60 dark:bg-zinc-800/40 p-3 min-h-[70px] space-y-2">
          <Line w="70%" />
          <Line w="55%" />
        </div>
        <Button disabled className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-sm font-semibold">
          <Sparkles className="w-4 h-4 mr-2" /> {t('demoTabs.generate') || 'Generate'}
        </Button>
      </div>
    </SectionWrapper>
  )
}

export const CutReplyTimePreview = () => {
  const { t } = useLanguage()
  return (
    <SectionWrapper
      title={t('preview.section.cutReplyTime.title') || 'Cut Reply Time by 90%'}
      subtitle={t('preview.section.cutReplyTime.subtitle') || 'Compare manual drafting vs AI assist. Parallel options + built-in translation slash revision loops.'}
    >
      <div className="grid md:grid-cols-2 gap-4 text-[11px] leading-relaxed">
        <div className="relative p-4 rounded-xl border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-800/60">
          <div className="absolute -top-2 left-3 bg-gray-200 dark:bg-zinc-700 text-gray-700 dark:text-gray-200 text-[10px] font-semibold px-2 py-0.5 rounded">{t('preview.manualFlow') || 'Manual Flow'}</div>
          <ul className="space-y-2 list-disc pl-4">
            <li>{t('preview.manualFlow.step1') || 'Plan structure & tone'}</li>
            <li>{t('preview.manualFlow.step2') || 'Draft opening / delete & rewrite'}</li>
            <li>{t('preview.manualFlow.step3') || 'Adjust politeness & grammar'}</li>
            <li>{t('preview.manualFlow.step4') || 'Lookup wording + verify translation'}</li>
            <li>{t('preview.manualFlow.step5') || 'Paste into target app'}</li>
          </ul>
          <div className="mt-4 flex items-center gap-1 text-rose-600 dark:text-rose-400 font-medium">
            <Clock className="w-3.5 h-3.5" /> {t('preview.time.manual') || '5–8 min'}
          </div>
        </div>
        <div className="relative p-4 rounded-xl border border-blue-200 dark:border-indigo-700/70 bg-gradient-to-br from-blue-50 via-white to-white dark:from-indigo-950/30 dark:via-zinc-900/40 dark:to-zinc-900">
          <div className="absolute -top-2 left-3 bg-blue-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded shadow">{t('preview.aiAssisted') || 'AI Assisted'}</div>
          <ol className="space-y-2 list-decimal pl-4 marker:text-blue-600 dark:marker:text-indigo-400">
            <li>{t('preview.step.pasteOrTemplate') || 'Paste request or pick template'}</li>
            <li>{t('preview.step.optionalDescription') || '(Optional) add description / scenario'}</li>
            <li>{t('preview.step.pickOption') || 'Pick one generated option'}</li>
            <li>{t('preview.step.copyShareTranslate') || 'One-click copy / share / translate'}</li>
          </ol>
          <div className="mt-4 flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
            <Clock className="w-3.5 h-3.5" /> {t('preview.time.ai') || '30–40 sec'}
          </div>
        </div>
      </div>
    </SectionWrapper>
  )
}

export const CopySharePreview = () => {
  const { t } = useLanguage()
  const options = [
    { id: 1, text: 'Hi Michael, just a quick update: the integration is 90% done and we\'ll finalize QA tomorrow.' },
    { id: 2, text: 'Quick progress note — core features are implemented; only performance tuning left. Ready for stakeholder review.' }
  ]
  const Line = ({ w }: { w: string }) => (
    <div className="h-3 rounded bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 dark:from-indigo-800 dark:via-indigo-700 dark:to-indigo-800 animate-pulse" style={{ width: w }} />
  )
  return (
    <SectionWrapper
      title={t('preview.section.copyShare.title') || 'One-click Copy & Share'}
      subtitle={t('preview.section.copyShare.subtitle') || 'Multiple options laid out with unified top actions: copy all / share / per-option copy. Translation appended automatically.'}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" className="h-8 text-xs font-medium"><Copy className="w-3.5 h-3.5 mr-1" />{t('preview.copyAll') || 'Copy All'}</Button>
      <Button size="sm" variant="outline" className="h-8 text-xs font-medium"><Share2 className="w-3.5 h-3.5 mr-1" />{t('preview.share') || 'Share'}</Button>
    <div className="ml-auto text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('preview.enPlusTranslation') || 'EN + Translation'}</div>
        </div>
        <div className="grid gap-2">
          {options.map(o => (
            <div key={o.id} className="relative group p-3 rounded-lg border border-slate-200 dark:border-zinc-700 bg-white/90 dark:bg-zinc-800/70 hover:border-blue-300 dark:hover:border-indigo-500 transition">
              <div className="pr-12 space-y-2" aria-label={o.text}>
                <Line w="90%" />
                <Line w="70%" />
              </div>
              <button className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-blue-600 hover:bg-blue-700 text-white rounded-md px-2 py-1 text-[10px] font-medium flex items-center gap-1">
                <Copy className="w-3 h-3" />{t('interface.copy') || 'Copy'}
              </button>
            </div>
          ))}
        </div>
  <div className="rounded-md bg-gray-50 dark:bg-zinc-800/50 p-2 text-[10px] text-gray-500 dark:text-gray-400">{t('preview.translationAppendNote') || '(*) Translation is appended automatically when copying all.'}</div>
      </div>
    </SectionWrapper>
  )
}

export const AllPreviews = () => (
  <div className="grid gap-6 lg:grid-cols-2">
    <ScenarioTemplatesPreview />
    <CustomInputsPreview />
    <CutReplyTimePreview />
    <CopySharePreview />
  </div>
)

export default AllPreviews
